import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * useDelayedModal
 *
 * Custom React hook to manage modal visibility with a delay.
 *
 * @param shouldShow - External trigger indicating that the modal should open.
 * @param delay - Time in milliseconds to wait before showing the modal (default: 2000ms).
 *
 * @returns An object containing:
 *   - show: boolean indicating whether the modal is currently visible.
 *   - hide: function to immediately hide the modal and clear any pending timeout.
 *
 * Behavior:
 *   - When `shouldShow` becomes true, `show` will turn true after the specified delay.
 *   - If `shouldShow` becomes false before the delay, the modal will not open.
 *   - Calling `hide` manually will immediately hide the modal and cancel any pending timeout.
 *   - The returned object is memoized, ensuring stable references for dependencies in other hooks or memoized components.
 *
 * Example:
 *   const { show, hide } = useDelayedModal(shouldShow, 2000);
 */

function useDelayedModal(shouldShow: boolean, delay = 2000) {
  const [show, setShow] = useState(false);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (!timeoutIdRef.current) return;

    clearTimeout(timeoutIdRef.current);
    timeoutIdRef.current = null;
  }, []);

  useEffect(() => {
    clear();

    if (!shouldShow) {
      setShow(false);

      return;
    }

    timeoutIdRef.current = setTimeout(() => {
      setShow(true);
      timeoutIdRef.current = null;
    }, delay);

    return clear;
  }, [shouldShow, delay, clear]);

  const hide = useCallback(() => {
    clear();
    setShow(false);
  }, [clear]);

  return useMemo(() => ({ show, hide }), [show, hide]);
}

export default useDelayedModal;
